/**
 * OFC Fantasy Mode
 *
 * Fantasy qualification rules vary by variant:
 * - Standard Pineapple: QQ+ in front
 * - 2-7 Pineapple: KK+ in front OR perfect 2-7 middle (2-3-4-5-7 no pair)
 * - Progressive: QQ+ in front with scaled card counts
 *
 * Card counts:
 * - Standard: 14 cards (17 for trips)
 * - Progressive: QQ=14, KK=15, AA=16, Trips=17
 * - 2-7 Double Fantasy: 15 cards
 *
 * Re-fantasy (staying in fantasy) requires:
 * - Trips in front, OR
 * - Quads+ in back
 */
import { isBoardComplete, isBoardFouled } from './board';
import { getRankIndex } from './card';
import { Label, OFCVariant, Rank } from './enums';
import { evaluateFrontHand, evaluateHighHand, getRankCounts } from './hands';
import { Board, Card } from './types';
import { getVariantConfig } from './variants';

// ============================================================================
// Helper Functions
// ============================================================================

function hasPairOrBetter(cards: readonly Card[], minRank: Rank): boolean {
  const counts = getRankCounts(cards);
  for (const [rank, count] of counts) {
    if (count >= 2 && getRankIndex(rank) >= getRankIndex(minRank)) {
      return true;
    }
  }
  return false;
}

function hasTrips(cards: readonly Card[]): boolean {
  const counts = getRankCounts(cards);
  for (const count of counts.values()) {
    if (count >= 3) {
      return true;
    }
  }
  return false;
}

function getHighestPairRank(cards: readonly Card[]): Rank | null {
  const counts = getRankCounts(cards);
  let highestPairRank: Rank | null = null;
  let highestPairIndex = -1;

  for (const [rank, count] of counts) {
    if (count >= 2) {
      const rankIndex = getRankIndex(rank);
      if (rankIndex > highestPairIndex) {
        highestPairIndex = rankIndex;
        highestPairRank = rank;
      }
    }
  }

  return highestPairRank;
}

function hasQuadsOrBetter(cards: readonly Card[]): boolean {
  if (cards.length !== 5) return false;

  const entry = evaluateHighHand(cards);
  return (
    entry.label === Label.FOUR_OF_A_KIND || entry.label === Label.STRAIGHT_FLUSH || entry.label === Label.ROYAL_FLUSH
  );
}

/**
 * Check if middle row is a perfect 2-7 hand (2-3-4-5-7 with no pair)
 * Used for 2-7 fantasy entry via middle row
 */
export function isPerfect27Middle(cards: readonly Card[]): boolean {
  if (cards.length !== 5) return false;

  const counts = getRankCounts(cards);

  // Must have no pairs
  for (const count of counts.values()) {
    if (count > 1) return false;
  }

  // Must have exactly 2, 3, 4, 5, 7 (the best 2-7 low hand)
  const requiredRanks = [Rank.DEUCE, Rank.TREY, Rank.FOUR, Rank.FIVE, Rank.SEVEN];
  for (const rank of requiredRanks) {
    if (!counts.has(rank)) return false;
  }

  return true;
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Check if a completed board qualifies for fantasy entry
 * Uses variant config to determine entry rules
 */
export function qualifiesForFantasy(board: Board, variant: OFCVariant): boolean {
  const config = getVariantConfig(variant);

  if (!config.fantasy?.enabled) {
    return false;
  }

  if (!isBoardComplete(board)) {
    return false;
  }

  if (isBoardFouled(board, variant)) {
    return false;
  }

  // Check front entry (all fantasy variants)
  if (hasPairOrBetter(board.front.cards, config.fantasy.entryMinRank)) {
    return true;
  }

  // Check 2-7 middle entry
  if (config.fantasy.allows27MiddleEntry && isPerfect27Middle(board.middle.cards)) {
    return true;
  }

  return false;
}

/**
 * Check if board qualifies via front row
 */
export function qualifiesViaFront(board: Board, variant: OFCVariant): boolean {
  const config = getVariantConfig(variant);
  if (!config.fantasy?.enabled) return false;
  if (!isBoardComplete(board) || isBoardFouled(board, variant)) return false;

  return hasPairOrBetter(board.front.cards, config.fantasy.entryMinRank);
}

/**
 * Check if board qualifies via middle row (2-7 only)
 */
export function qualifiesViaMiddle(board: Board, variant: OFCVariant): boolean {
  const config = getVariantConfig(variant);
  if (!config.fantasy?.enabled) return false;
  if (!config.fantasy.allows27MiddleEntry) return false;
  if (!isBoardComplete(board) || isBoardFouled(board, variant)) return false;

  return isPerfect27Middle(board.middle.cards);
}

/**
 * Check if board is double fantasy (2-7: both front and middle qualify)
 */
export function isDoubleFantasy(board: Board, variant: OFCVariant): boolean {
  const config = getVariantConfig(variant);
  if (!config.fantasy?.allows27MiddleEntry) return false;

  return qualifiesViaFront(board, variant) && qualifiesViaMiddle(board, variant);
}

/**
 * Get the number of fantasy cards based on board and variant
 */
export function getFantasyCardCount(board: Board, variant: OFCVariant): number {
  if (!qualifiesForFantasy(board, variant)) {
    return 0;
  }

  const config = getVariantConfig(variant);

  // 2-7 double fantasy: 15 cards
  if (isDoubleFantasy(board, variant)) {
    return 15;
  }

  // Progressive card counts: QQ=14, KK=15, AA=16, Trips=17
  if (config.fantasy?.progressiveCardCounts) {
    if (hasTrips(board.front.cards)) {
      return 17;
    }
    const pairRank = getHighestPairRank(board.front.cards);
    if (pairRank === Rank.ACE) {
      return 16;
    }
    if (pairRank === Rank.KING) {
      return 15;
    }
    return 14;
  }

  // Standard pineapple: 14 cards (17 for trips)
  if (hasTrips(board.front.cards)) {
    return 17;
  }

  return 14;
}

/**
 * Check if a player continues fantasy (re-fantasy)
 * Must have trips in front OR quads+ in back
 * Same rule for all variants
 */
export function continuesFantasy(board: Board, variant?: OFCVariant): boolean {
  if (!isBoardComplete(board)) {
    return false;
  }

  if (isBoardFouled(board, variant)) {
    return false;
  }

  if (hasTrips(board.front.cards)) {
    return true;
  }

  if (hasQuadsOrBetter(board.back.cards)) {
    return true;
  }

  return false;
}

/**
 * Get complete fantasy trigger info for a board
 */
export interface FantasyTriggerInfo {
  qualifies: boolean;
  qualifiesViaFront: boolean;
  qualifiesViaMiddle: boolean;
  isDouble: boolean;
  cardCount: number;
  continues: boolean;
  reason: string | null;
}

export function getFantasyTriggerInfo(board: Board, variant: OFCVariant): FantasyTriggerInfo {
  if (!isBoardComplete(board)) {
    return {
      qualifies: false,
      qualifiesViaFront: false,
      qualifiesViaMiddle: false,
      isDouble: false,
      cardCount: 0,
      continues: false,
      reason: null,
    };
  }

  if (isBoardFouled(board, variant)) {
    return {
      qualifies: false,
      qualifiesViaFront: false,
      qualifiesViaMiddle: false,
      isDouble: false,
      cardCount: 0,
      continues: false,
      reason: 'Board is fouled',
    };
  }

  const viaFront = qualifiesViaFront(board, variant);
  const viaMiddle = qualifiesViaMiddle(board, variant);
  const qualifies = viaFront || viaMiddle;
  const isDouble = viaFront && viaMiddle;
  const cardCount = getFantasyCardCount(board, variant);
  const continues = continuesFantasy(board, variant);

  let reason: string | null = null;
  if (qualifies) {
    if (isDouble) {
      reason = 'Double fantasy: Front + Middle';
    } else if (viaFront) {
      const frontEntry = evaluateFrontHand(board.front.cards);
      reason = `Front: ${frontEntry.label}`;
    } else if (viaMiddle) {
      reason = 'Middle: Perfect 2-7';
    }
  }

  return {
    qualifies,
    qualifiesViaFront: viaFront,
    qualifiesViaMiddle: viaMiddle,
    isDouble,
    cardCount,
    continues,
    reason,
  };
}
